import { utils, writeFile } from 'xlsx';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, from, of } from 'rxjs';
import { catchError, map, mergeMap, takeUntil } from 'rxjs/operators';
import { DashboardTeudotResponse, StoreService } from './store.service';
import { ResponseFromExpress } from '../Interfaces/sqlresponses';

export type ExcelObject = {
  data: any[];
  headers?: string[];
  filename: string;
  extension: 'csv' | 'xlsx';
};

export type ActiveClientStatistics = {
  client: string;
  activeAgents?: number;
  totalAgents?: number;
  error?: boolean;
  invalidRequest?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class ExcelService {
  constructor(private _ss: StoreService) {}

  totalRequests$ = new BehaviorSubject([]);
  requestResult$ = new BehaviorSubject([]);
  anyRequests$ = this.totalRequests$.pipe(map((results) => results.length));
  totalRequestsMade$ = this.requestResult$.pipe(
    map((results) => results.length)
  );

  downloadComplete$ = new BehaviorSubject(false);

  downloadReport(clients: ActiveClientStatistics[]) {
    const dataObject: ExcelObject = {
      data: [],
      filename: '',
      extension: 'xlsx',
    };

    dataObject.filename = `דוח סוכנים פעילים - ${Intl.DateTimeFormat('he-IL')
      .format(new Date())
      .replace(/\./g, '-')}`;

    dataObject.data = clients.map((client) => {
      return {
        לקוח: client.client,
        'סוכנים פעילים בחודשיים האחרונים': client.error
          ? 'לא ידוע'
          : client.activeAgents,
        'סה"כ סוכנים': client.totalAgents ?? 'לא ידוע',
        'שרת החזיר תשובה': client.error && !client.invalidRequest ? 'לא' : 'כן',
      };
    });

    const sheet = utils.json_to_sheet(dataObject.data);

    const wb = utils.book_new();

    utils.book_append_sheet(wb, sheet);

    // console.log({ wb });

    if (!wb.Workbook) wb.Workbook = {};
    if (!wb.Workbook.Views) wb.Workbook.Views = [{}];
    wb.Workbook.Views[0].RTL = true;

    writeFile(wb, `${dataObject.filename}.${dataObject.extension}`, {
      bookType: dataObject.extension,
      compression: true,
      cellStyles: true,
    });
  }

  generateReport() {
    if (this.totalRequests$.value.length) {
      return;
    }
    this.totalRequests$.next([]);
    this.requestResult$.next([]);
    this.downloadComplete$.next(false);

    const customers$ = this._ss.customers$.value.map((cust) => {
      return {
        observables: forkJoin({
          teudot: this._ss.GetTeudotDashbordCustomServer(cust.CustServer),
          agents: this._ss.GetAllAgentsCustomServer(cust.CustServer),
        }).pipe(
          catchError((e) => {
            console.log(e);
            return of('error');
          })
        ),
        client: `${cust.CustName} - (${cust.CustNum})`,
      };
    });

    const MAX_PARALLEL_QUERIES = 4;
    const DAY = 1000 * 60 * 60 * 24;
    const SIXTY_ONE_DAYS = DAY * 61;
    this.totalRequests$.next(customers$);
    from(customers$)
      .pipe(
        mergeMap(
          ({ observables, client }) =>
            observables.pipe(
              map((res) => {
                return typeof res === 'string'
                  ? ['error', 'error', client]
                  : [res.teudot, res.agents, client];
              })
            ),
          MAX_PARALLEL_QUERIES
        )
      )
      .subscribe({
        next: ([teudot, agents, client]: [
          ResponseFromExpress<DashboardTeudotResponse> | 'error',
          ResponseFromExpress<any> | 'error',
          string
        ]) => {
          const value = this.requestResult$.value;
          if (typeof teudot === 'string' || typeof agents === 'string') {
            this.requestResult$.next([
              ...value,
              {
                client,
                error: true,
                invalidRequest: teudot !== 'error' && agents !== 'error',
              },
            ]);
            return;
          }

          const length = teudot.recordset.length && agents.recordset.length;
          if (!length) {
            this.requestResult$.next([
              ...value,
              {
                client,
                activeAgents: 0,
                totalAgents: 0,
                error: false,
              },
            ]);
            return;
          }

          this.requestResult$.next([
            ...value,
            {
              client,
              activeAgents: new Set(
                teudot.recordset
                  .filter(
                    (teuda) =>
                      teuda.TeudaDate &&
                      new Date(teuda.TeudaDate).getTime() >
                        new Date().getTime() - SIXTY_ONE_DAYS
                  )
                  .map((teuda) => teuda.AgentID)
              ).size,
              totalAgents: agents.rowsAffected[0],
              error: false,
            },
          ]);
        },
        complete: () => {
          const results = this.requestResult$.value;

          this.downloadReport(results);

          this.totalRequests$.next([]);
          this.requestResult$.next([]);
          this.downloadComplete$.next(true);
        },
      });
  }
}
